import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Default UI shown when an ErrorBoundary catches a render error.
 * `variant`: "root" | "app" | "page" — controls copy density / actions.
 */
export default function ErrorFallback({
  error,
  resetErrorBoundary,
  variant = "page",
}) {
  const navigate = useNavigate();
  const isDev = process.env.NODE_ENV === "development";
  const message =
    error?.message || "Something went wrong while loading this view.";

  const handleHome = () => {
    resetErrorBoundary?.();
    navigate("/dashboard", { replace: true });
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleRetry = () => {
    resetErrorBoundary?.();
  };

  const minHeight =
    variant === "page" ? "min-h-[50vh]" : "min-h-screen";

  return (
    <div
      className={`flex ${minHeight} w-full items-center justify-center px-4 py-10`}
      role="alert"
    >
      <div className="w-full max-w-md rounded-xl border border-[#e2e8f0] bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">
          Unexpected error
        </p>
        <h1 className="mt-2 text-xl font-semibold text-[#132238]">
          {variant === "page"
            ? "This page failed to load"
            : "Something went wrong"}
        </h1>
        <p className="mt-2 text-sm text-[#64748b]">
          {variant === "page"
            ? "You can try again, go back to the dashboard, or reload the app."
            : "The app hit an unexpected error. Try again or reload."}
        </p>

        {isDev && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-lg bg-[#f8fafc] p-3 text-xs text-[#b91c1c] whitespace-pre-wrap break-words">
            {message}
          </pre>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          {typeof resetErrorBoundary === "function" && (
            <button
              type="button"
              onClick={handleRetry}
              className="rounded-lg bg-[#0a64f9] px-4 py-2 text-sm font-medium text-white hover:bg-[#0854d4]"
            >
              Try again
            </button>
          )}
          {variant !== "root" && (
            <button
              type="button"
              onClick={handleHome}
              className="rounded-lg border border-[#d7e0ea] bg-white px-4 py-2 text-sm font-medium text-[#132238] hover:bg-[#f8fafc]"
            >
              Go to dashboard
            </button>
          )}
          <button
            type="button"
            onClick={handleReload}
            className="rounded-lg border border-[#d7e0ea] bg-white px-4 py-2 text-sm font-medium text-[#132238] hover:bg-[#f8fafc]"
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
